import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { 
  GraduationCap, 
  CheckCircle, 
  XCircle,
  Loader2,
  ArrowRight
} from "lucide-react";

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking"); // checking, success, failed
  const [userId, setUserId] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus("failed");
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("failed");
      toast.error("Payment verification timed out. Please contact support.");
      return;
    }

    setPollCount(attempts + 1);

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        setStatus("success");
        setUserId(response.data.user_id);
        toast.success("Payment successful!");
      } else if (response.data.payment_status === "failed" || response.data.status === "expired") {
        setStatus("failed");
        toast.error("Payment failed or expired.");
      } else {
        // Continue polling
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error("Error checking payment:", error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full bg-white rounded-2xl border border-stone-100 shadow-lg">
        <CardContent className="p-8 text-center">
          {status === "checking" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-violet-100 rounded-full flex items-center justify-center">
                <Loader2 className="h-10 w-10 text-violet-700 animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-stone-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                Verifying Payment
              </h2>
              <p className="text-stone-600 mb-4">
                Please wait while we confirm your payment...
              </p>
              <p className="text-sm text-stone-400">
                Attempt {pollCount} of 5
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-emerald-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-stone-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                Payment Successful!
              </h2>
              <p className="text-stone-600 mb-6">
                Your account has been created. Set your password to complete registration.
              </p>
              <Button 
                onClick={() => navigate(`/set-password?user_id=${userId}`)}
                className="w-full bg-violet-700 hover:bg-violet-800 h-12 rounded-xl"
                data-testid="set-password-btn"
              >
                Set Password <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </>
          )}

          {status === "failed" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="h-10 w-10 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-stone-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                Payment Failed
              </h2>
              <p className="text-stone-600 mb-6">
                We couldn't verify your payment. Please try again or contact support.
              </p>
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate('/register')}
                  className="w-full bg-violet-700 hover:bg-violet-800 h-12 rounded-xl"
                >
                  Try Again
                </Button>
                <Link to="/">
                  <Button variant="outline" className="w-full h-12 rounded-xl">
                    Back to Home
                  </Button>
                </Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
